import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CaretDown, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

const gradeColor = (g) => ({
  A: { bg: "bg-emerald-600", ring: "shadow-[6px_6px_0px_#065f46]", text: "text-emerald-700" },
  B: { bg: "bg-lime-600",    ring: "shadow-[6px_6px_0px_#3f6212]", text: "text-lime-700" },
  C: { bg: "bg-amber-500",   ring: "shadow-[6px_6px_0px_#92400e]", text: "text-amber-700" },
  D: { bg: "bg-orange-600",  ring: "shadow-[6px_6px_0px_#7c2d12]", text: "text-orange-700" },
  F: { bg: "bg-red-600",     ring: "shadow-[6px_6px_0px_#7f1d1d]", text: "text-red-700" },
}[g] || { bg: "bg-slate-500", ring: "shadow-[6px_6px_0px_#334155]", text: "text-slate-700" });

function Sparkline({ points }) {
  if (!points || points.length < 2) return <div className="text-[10px] italic text-slate-400">need 2+ days of history for trend</div>;
  const w = 220, h = 40, pad = 3;
  const min = Math.min(...points.map(p => p.score));
  const max = Math.max(...points.map(p => p.score));
  const range = Math.max(max - min, 1);
  const path = points.map((p, i) => {
    const x = pad + (i / (points.length - 1)) * (w - pad * 2);
    const y = h - pad - ((p.score - min) / range) * (h - pad * 2);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const first = points[0].score, last = points[points.length - 1].score;
  const delta = (last - first).toFixed(1);
  const trendIcon = delta > 0 ? <TrendUp weight="bold" size={12} className="text-emerald-600"/>
                  : delta < 0 ? <TrendDown weight="bold" size={12} className="text-red-600"/>
                  : <Minus weight="bold" size={12} className="text-slate-400"/>;
  return (
    <div className="flex items-center gap-2">
      <svg width={w} height={h} className="border border-slate-200 bg-white">
        <path d={path} stroke="#0f172a" strokeWidth="1.5" fill="none"/>
      </svg>
      <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
        {trendIcon} {delta > 0 ? "+" : ""}{delta} pts · {points.length}d
      </div>
    </div>
  );
}

function SubRow({ label, sub }) {
  const s = sub?.score;
  const bar = s == null ? 0 : s;
  const bg = s == null ? "bg-slate-300" : s >= 80 ? "bg-emerald-500" : s >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-200 last:border-0">
      <div className="w-32 flex-shrink-0">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-[9px] text-slate-400">weight {Math.round((sub?.weight || 0) * 100)}%</div>
      </div>
      <div className="flex-1">
        <div className="h-2 bg-slate-100 border border-slate-200 overflow-hidden">
          <div className={`h-full ${bg}`} style={{ width: `${bar}%` }}/>
        </div>
        <div className="mt-1 text-[10px] text-slate-600 italic">{sub?.label || "—"}</div>
      </div>
      <div className={`w-10 text-right font-black text-lg tabular-nums ${s == null ? "text-slate-300" : ""}`}>
        {s == null ? "—" : Math.round(s)}
      </div>
    </div>
  );
}

export default function PortfolioHealthScore({ slim = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portfolio-health")
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return slim ? null : (
    <div className="mt-4 border-2 border-slate-900 bg-white p-4 animate-pulse">
      <div className="h-6 w-40 bg-slate-200"/>
      <div className="mt-2 h-16 bg-slate-100"/>
    </div>
  );
  if (!data) return null;

  const { score, grade, subscores, trend } = data;
  const color = gradeColor(grade);

  // ---- Slim mode: single-line tap row ----
  if (slim) {
    return (
      <div data-testid="portfolio-health-score-slim" className="mt-3">
        <button
          onClick={() => setOpen(v => !v)}
          data-testid="portfolio-health-toggle"
          className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border ${grade === "F" || grade === "D" ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"} hover:bg-slate-50 transition-colors`}
        >
          <span className={`${color.bg} text-white w-8 h-8 rounded flex items-center justify-center text-[13px] font-black flex-shrink-0`}>{grade}</span>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-black text-slate-900 truncate">
              Portfolio Health · <span className="tabular-nums">{Math.round(score)}</span>
              <span className={`ml-1.5 text-[10px] uppercase tracking-widest font-black ${color.text}`}>Grade {grade}</span>
            </div>
            <div className={`text-[10px] font-bold ${color.text} truncate`}>
              {score >= 90 ? "Elite operator" :
               score >= 80 ? "Strong shape — squeeze the edges" :
               score >= 70 ? "Solid but leaking hours" :
               score >= 60 ? "Fires need putting out this week" :
               "Something's on fire — tap to see what"}
            </div>
          </div>
          <CaretDown weight="bold" size={16} className={`flex-shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}/>
        </button>
        {open && (
          <div className="mt-2 border border-slate-200 rounded-lg p-3 bg-white">
            <SubRow label="Occupancy"        sub={subscores.occupancy}/>
            <SubRow label="Delinquency"      sub={subscores.delinquency}/>
            <SubRow label="Maint Backlog"    sub={subscores.backlog}/>
            <SubRow label="Turnover Speed"   sub={subscores.turnover}/>
            <SubRow label="Vendor Payables"  sub={subscores.vendor_payables}/>
            <div className="mt-2">
              <Sparkline points={trend}/>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="portfolio-health-score"
      className={`mt-4 border-2 border-slate-900 bg-white ${color.ring} transition-transform hover:-translate-y-0.5`}>
      <button onClick={() => setOpen(v => !v)}
        data-testid="portfolio-health-toggle"
        className="w-full text-left p-4 flex items-center gap-4">
        <div className={`${color.bg} text-white w-20 h-20 flex flex-col items-center justify-center border-2 border-slate-900 flex-shrink-0`}>
          <div className="text-3xl font-display font-black leading-none">{Math.round(score)}</div>
          <div className="text-[10px] font-black uppercase tracking-widest opacity-90">grade {grade}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-xl tracking-tight">Portfolio Health</div>
          <div className={`text-xs font-bold uppercase tracking-wider ${color.text}`}>
            {score >= 90 ? "Elite operator" :
             score >= 80 ? "Strong shape — squeeze the edges" :
             score >= 70 ? "Solid but leaking hours" :
             score >= 60 ? "Fires need putting out this week" :
             "Something's on fire — tap to see what"}
          </div>
          <div className="mt-2">
            <Sparkline points={trend}/>
          </div>
        </div>
        <CaretDown weight="bold" size={20} className={`flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="border-t-2 border-slate-900 p-4 bg-slate-50">
          <SubRow label="Occupancy"        sub={subscores.occupancy}/>
          <SubRow label="Delinquency"      sub={subscores.delinquency}/>
          <SubRow label="Maint Backlog"    sub={subscores.backlog}/>
          <SubRow label="Turnover Speed"   sub={subscores.turnover}/>
          <SubRow label="Vendor Payables"  sub={subscores.vendor_payables}/>
          <div className="mt-3 text-[10px] uppercase tracking-widest font-black text-slate-400">
            Score updates every time you open the dashboard · snapshot saved daily
          </div>
        </div>
      )}
    </div>
  );
}
